// Money: P&L, per-product profit contribution, ledger, financing.

import { useState } from "react";
import type { LedgerCategory } from "../../engine/types.js";
import { interpolate } from "../../engine/calendar.js";
import { MIN_RAISE, MAX_RAISE } from "../../engine/commands.js";
import type { ScreenProps } from "./shared.js";
import { money, monthLabel, num, pct } from "../format.js";
import { BarChart, MoneyInput, MultiLineChart } from "../components/widgets.js";

const CATEGORY_LABEL: Record<LedgerCategory, string> = {
  payroll: "payroll",
  tools: "EDA / tools",
  gna: "G&A",
  recruiting: "recruiting",
  severance: "severance",
  maskNre: "mask sets",
  nodeQualFee: "node qualification",
  financingLegal: "financing legal",
  cogs: "cost of goods",
  recall: "field-failure recalls",
  revenue: "product revenue",
  nre: "NRE consulting",
  financing: "financing",
  s1Costs: "S-1 costs",
  ipoProceeds: "IPO proceeds",
  ipoFees: "underwriting fees",
  secondary: "secondary offering",
  buyback: "buybacks",
  publicCompany: "public-company costs",
  priceProtection: "channel price protection",
  channelReturns: "EOL channel returns",
  ipUpfront: "IP license fees",
  ipRoyalty: "IP royalties",
  retention: "retention grants",
  signingBonus: "signing bonuses",
  liftOut: "team lift-outs",
  acquisition: "acquisitions",
  volumePenalty: "volume-commit penalties",
  askCash: "stakeholder decisions",
  incorporation: "incorporation costs",
  treeCash: "research capital purchases",
  devContract: "development contract",
  socketNre: "socket royalties",
};

export function Money({ snap, content, session }: ScreenProps) {
  const { view, report, pending } = snap;
  const fin = view.finance;
  const kpi = view.kpiHistory[view.kpiHistory.length - 1];
  const ttm = fin.ttmRevenue.reduce((a, b) => a + b, 0);
  const [ask, setAsk] = useState(3_000_000);
  const raiseQueued = pending.some((c) => c.type === "raiseRound");
  const sentiment = interpolate(content.economy.sentiment, view.monthIndex);

  if (view.stage === "garage" && view.garage) return <MoneyLite snap={snap} content={content} session={session} />;

  const byCategory = new Map<LedgerCategory, number>();
  if (report) for (const e of report.ledger) byCategory.set(e.category, (byCategory.get(e.category) ?? 0) + e.amount);

  return (
    <div>
      <h2>MONEY</h2>

      <div className="row">
        <div className="tile">
          <div className="k">CASH</div>
          <div className="v">{money(view.cash)}</div>
          <div className="sub">runway {kpi ? (kpi.runwayMonths >= 999 ? "∞" : `${kpi.runwayMonths.toFixed(0)}mo`) : "—"}</div>
        </div>
        <div className="tile">
          <div className="k">TTM PRODUCT REVENUE</div>
          <div className="v">{money(ttm)}</div>
          <div className="sub">what VCs price</div>
        </div>
        <div className="tile">
          <div className="k">RAISED TO DATE</div>
          <div className="v">{money(fin.totalRaised)}</div>
          <div className="sub">{fin.roundsCompleted} rounds closed</div>
        </div>
        <div className="tile">
          <div className="k">FOUNDER SHARE</div>
          <div className="v">{pct(fin.founderShare, 1)}</div>
          <div className="sub">dilution is forever</div>
        </div>
      </div>

      <div className="panel">
        <div className="title">FINANCING</div>
        {fin.activeRaise ? (
          <div>
            Round open: asking {money(fin.activeRaise.ask)} — month {fin.activeRaise.monthsActive + 1} of investor meetings. Closing odds
            ride sentiment ({pct(sentiment, 0)}) and your traction ({fin.everWorkingSilicon ? "working silicon ✓" : "pre-silicon"}
            {ttm > 2_000_000 ? `, ${money(ttm)} TTM` : ""}).
          </div>
        ) : fin.raiseCooldownMonths > 0 ? (
          <div className="hint">Investors need a breather — {fin.raiseCooldownMonths} months before you can pitch again.</div>
        ) : (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <label>
              ask $<MoneyInput value={ask} onChange={setAsk} min={MIN_RAISE} max={MAX_RAISE} />
            </label>
            <button className={`act warn${raiseQueued ? " queued" : ""}`} onClick={() => session.queue({ type: "raiseRound", ask })}>
              {raiseQueued ? "ROUND QUEUED" : `OPEN A ROUND (legal ${money(content.economy.financing.legalCost)})`}
            </button>
            <span className="hint">closes as a monthly hazard; withdrawn after {content.economy.financing.maxMonths} months</span>
          </div>
        )}
      </div>

      <DevContractPanel snap={snap} content={content} session={session} />

      <ProductProfit snap={snap} content={content} session={session} />

      <div className="panel">
        <div className="title">REVENUE / COGS / OPEX — TRAILING {Math.min(48, view.kpiHistory.length)}MO</div>
        <MultiLineChart
          width={420}
          height={90}
          series={[
            { label: "revenue", color: "var(--ink)", data: view.kpiHistory.slice(-48).map((k) => k.revenue) },
            { label: "COGS", color: "var(--amber)", data: view.kpiHistory.slice(-48).map((k) => k.cogs) },
            { label: "opex", color: "var(--market-1)", data: view.kpiHistory.slice(-48).map((k) => k.opex) },
          ]}
        />
      </div>

      {report && (
        <div className="panel">
          <div className="title">LAST MONTH — {monthLabel(report.month)}</div>
          <BarChart
            width={420}
            items={[...byCategory.entries()].sort((a, b) => b[1] - a[1]).map(([cat, amt]) => ({ label: CATEGORY_LABEL[cat], value: amt }))}
            formatValue={(v) => money(v)}
          />
          <div className="scroll-x">
            <table>
              <thead>
                <tr>
                  <th>category</th>
                  <th className="r">amount</th>
                </tr>
              </thead>
              <tbody>
                {[...byCategory.entries()]
                  .sort((a, b) => b[1] - a[1])
                  .map(([cat, amt]) => (
                    <tr key={cat}>
                      <td>{CATEGORY_LABEL[cat]}</td>
                      <td className={`r${amt < 0 ? "" : ""}`} style={{ color: amt >= 0 ? "var(--ink)" : "var(--amber)" }}>
                        {money(amt)}
                      </td>
                    </tr>
                  ))}
                <tr>
                  <td>
                    <b>net</b>
                  </td>
                  <td className="r">
                    <b>{money([...byCategory.values()].reduce((a, b) => a + b, 0))}</b>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="title">SCORE</div>
        <span className="hint">
          Final score = founder share × (cash + 2 × TTM revenue) if the company survives to Dec 2008; zero if it dies. Every raise trades
          dilution for survival — both matter.
        </span>
      </div>
    </div>
  );
}

/** MONEY-lite (P1, spec §11): the garage has no P&L — just the ramen ledger.
 *  The full money screen unlocks with the cap table on incorporation day. */
function MoneyLite({ snap, content }: ScreenProps) {
  const { view } = snap;
  const g = view.garage!;
  const gdef = content.scenario.garage!;
  const cfg = content.economy.garage;
  const m = view.monthIndex;
  const burn = g.founders.length * gdef.personalBurnMonthly;
  return (
    <div>
      <h2>MONEY — SUCH AS IT IS</h2>
      <div className="row">
        <div className="tile">
          <div className="k">POOLED SAVINGS + THE POT</div>
          <div className={`v${g.savings <= 0 ? " bad" : g.savings < 6 * burn ? " warn" : ""}`}>{money(g.savings)}</div>
          <div className="sub">one runway, {g.founders.length} people</div>
        </div>
        <div className="tile">
          <div className="k">THE RAMEN CLOCK</div>
          <div className="v">−{money(burn)}/mo</div>
          <div className="sub">{money(gdef.personalBurnMonthly)} per founder, every month, no matter what</div>
        </div>
        <div className="tile">
          <div className="k">CONSULTING RATE</div>
          <div className="v">{money(interpolate(cfg.consultingRatePerFm, m))}/fm</div>
          <div className="sub">moonlighting adds to savings — at the cost of the time it buys</div>
        </div>
      </div>
      <div className="panel">
        <div className="title">NO COMPANY, NO P&L</div>
        <span className="hint">
          There is no payroll, no cap table, no board — and no score if you fold ($0; the napkins go in a drawer). The seed check on a signed
          term sheet becomes company cash, minus {money(cfg.pitch.incorporationCost)} of incorporation costs. This screen grows up when you do.
        </span>
      </div>
    </div>
  );
}

/** The strategic principal's development contract (P0-lite: signed with the
 *  seed when the thesis matched; P1 shows the ledger it rides on). */
function DevContractPanel({ snap, content }: ScreenProps) {
  const { view } = snap;
  const dc = view.devContract;
  if (!dc) return null;
  const def = content.scenario.devContracts?.find((d) => d.id === dc.defId);
  if (!def) return null;
  const m = view.monthIndex;
  const nreEnd = def.nreEndMonth ?? def.royaltyStartMonth;
  const phase = dc.cancelled
    ? "cancelled"
    : m < nreEnd
      ? "NRE"
      : m <= def.royaltyEndMonth
        ? m >= def.royaltyStartMonth
          ? "royalties"
          : "waiting on the socket"
        : "expired";
  return (
    <div className="panel">
      <div className="title">DEVELOPMENT CONTRACT — {def.principal.toUpperCase()}</div>
      {dc.cancelled ? (
        <span className="bad-text">
          Cancelled. The walk-away fee cleared{def.cancelFee ? ` (${money(def.cancelFee)})` : ""}; the socket is gone and nothing more pays.
        </span>
      ) : (
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
          <span>
            phase: <b>{phase}</b>
          </span>
          {m < nreEnd && (
            <span>
              NRE {money(def.nreMonthly)}/mo until {monthLabel(nreEnd)} · their feature list costs {def.nreEmMonthly} design EM/mo
            </span>
          )}
          <span>
            committed socket: {money(def.royaltyMonthly)}/mo {monthLabel(def.royaltyStartMonth)}–{monthLabel(def.royaltyEndMonth)}
            <span className="dim"> (royalties count as traction; NRE doesn't)</span>
          </span>
        </div>
      )}
    </div>
  );
}

/** Per-product contribution to last month's profit: market actuals from the tick
 *  report plus the estimated sustaining-engineering tax each ACTIVE product costs.
 *  Negative net contributors are the EOL candidates. */
function ProductProfit({ snap, content, session }: ScreenProps) {
  const { view, report, pending } = snap;
  if (!report || view.products.filter((p) => p.active).length === 0) return null;
  const eco = content.economy;
  const m = view.monthIndex;
  // sustaining tax per active product: design EM × loaded monthly design salary
  const designSalary = interpolate(
    eco.salaries.map((p) => ({ m: p.m, v: p.design })),
    m,
  );
  const sustainCost = eco.models.sustainingEmPerProduct * (designSalary / 12) * eco.overheadBurden;

  const rows = view.products
    .filter((p) => p.active)
    .map((p) => {
      const sale = report.productSales.find((s) => s.productId === p.id);
      const revenue = sale?.revenue ?? 0;
      const cogs = sale?.cogs ?? 0;
      const recall = sale?.recall ?? 0;
      const gross = revenue - cogs - recall;
      return { p, sale, revenue, cogs, recall, gross, net: gross - sustainCost };
    })
    .sort((a, b) => b.net - a.net);
  const total = rows.reduce(
    (a, r) => ({ revenue: a.revenue + r.revenue, cogs: a.cogs + r.cogs, recall: a.recall + r.recall, net: a.net + r.net }),
    { revenue: 0, cogs: 0, recall: 0, net: 0 },
  );

  return (
    <div className="panel">
      <div className="title">PROFIT BY PRODUCT — {monthLabel(report.month)}</div>
      <div className="scroll-x">
        <table>
          <thead>
            <tr>
              <th>product</th>
              <th>market</th>
              <th className="r">units</th>
              <th className="r">ASP</th>
              <th className="r">revenue</th>
              <th className="r">COGS</th>
              <th className="r">recalls</th>
              <th className="r">gross</th>
              <th className="r">GM%</th>
              <th className="r">sustain*</th>
              <th className="r">net/mo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ p, sale, revenue, cogs, recall, gross, net }) => {
              const eolQueued = pending.some((c) => c.type === "endOfLifeProduct" && c.productId === p.id);
              return (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>{content.markets.find((mk) => mk.id === p.marketId)?.displayName.split(" (")[0]}</td>
                  <td className="r">{sale ? num(sale.units) : "—"}</td>
                  <td className="r">{sale ? `$${sale.asp.toFixed(2)}` : "—"}</td>
                  <td className="r">{money(revenue)}</td>
                  <td className="r">{money(cogs)}</td>
                  <td className="r">{recall > 0 ? <span className="bad-text">{money(recall)}</span> : "—"}</td>
                  <td className="r">{money(gross)}</td>
                  <td className="r">{revenue > 0 ? pct((revenue - cogs) / revenue, 0) : "—"}</td>
                  <td className="r">{money(sustainCost)}</td>
                  <td className="r" style={{ color: net < 0 ? "var(--red)" : "var(--ink)" }}>
                    {money(net)}
                  </td>
                  <td>
                    <button
                      className={`act bad${eolQueued ? " queued" : ""}`}
                      onClick={() => session.queue({ type: "endOfLifeProduct", productId: p.id })}
                    >
                      {eolQueued ? "EOL QUEUED" : "EOL"}
                    </button>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td>
                <b>total</b>
              </td>
              <td></td>
              <td className="r"></td>
              <td className="r"></td>
              <td className="r">
                <b>{money(total.revenue)}</b>
              </td>
              <td className="r">
                <b>{money(total.cogs)}</b>
              </td>
              <td className="r">{total.recall > 0 ? money(total.recall) : "—"}</td>
              <td className="r">
                <b>{money(total.revenue - total.cogs - total.recall)}</b>
              </td>
              <td className="r">{total.revenue > 0 ? pct((total.revenue - total.cogs) / total.revenue, 0) : "—"}</td>
              <td className="r">{money(sustainCost * rows.length)}</td>
              <td className="r">
                <b style={{ color: total.net < 0 ? "var(--red)" : undefined }}>{money(total.net)}</b>
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
      <span className="hint">
        *sustain = each active product's sustaining-engineering tax ({eco.models.sustainingEmPerProduct} design EM/mo at loaded salary) —
        an estimate; it competes with your projects for designers either way. Products in the red are end-of-life candidates.
      </span>
    </div>
  );
}
